import React, { useContext, useState } from 'react';
import { Grid, Typography, Paper } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { I18nContext } from 'translations';
import { stopStatusColor, stopActionLabel, stopsAwayLabel, packagesCountLabel } from 'utils/deliveryRoute';
import PackageCard from './PackageCard';

export default function StopCard({ stop, onFocusStop, showCompany, selected }) {
  const { translate } = useContext(I18nContext);
  const [expanded, setExpanded] = useState(true);
  const color = stopStatusColor(stop.status);
  // stopsAwayLabel also covers the "already completed" case, but that's redundant here:
  // the status dot already says "Completado". Only worth a hint while it's still pending.
  const awayLabel = stop.status !== 'completed' ? stopsAwayLabel(stop, translate) : null;
  const hasPackages = stop.package_count > 0;

  return (
    <Paper
      elevation={0}
      className="delivery-route-stop-card"
      // Selected from the map: box-shadow on top of the border so the card doesn't shift.
      style={{
        padding: 16,
        marginBottom: 12,
        border: `1px solid ${selected ? '#9A68C2' : '#EDEAF0'}`,
        boxShadow: selected ? '0 0 0 1px #9A68C2' : 'none',
        backgroundColor: selected ? '#F7F2FB' : undefined,
        cursor: onFocusStop ? 'pointer' : 'default',
      }}
      data-testid="stop-card"
      data-stop-id={stop.id}
      data-selected={selected ? 'true' : 'false'}
      onClick={() => onFocusStop && onFocusStop(stop.id)}
    >
      <Grid container justify="space-between" alignItems="center">
        <Grid item>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: color,
              color: '#fff',
              fontWeight: 700,
              marginRight: 10,
            }}
          >
            {stop.display_number ?? stop.position}
          </span>
          <Typography component="span" variant="subtitle2" style={{ fontWeight: 700 }}>
            {stopActionLabel(stop.action, translate)}
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            component="span"
            variant="body2"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color, fontWeight: 700 }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, display: 'inline-block' }} />
            {stop.status_label}
          </Typography>
        </Grid>
      </Grid>

      <Typography variant="body2" color="textPrimary" style={{ marginTop: 8 }}>
        {stop.address}
        {stop.secondary_address && (
          <>
            <br />
            <small>{stop.secondary_address}</small>
          </>
        )}
      </Typography>

      {awayLabel && (
        <Typography
          variant="body2"
          data-testid="stop-card-stops-away"
          style={{ marginTop: 4, fontWeight: 600, color: '#9A68C2' }}
        >
          {awayLabel}
        </Typography>
      )}

      {hasPackages && (
        <>
          <Typography
            component="button"
            type="button"
            variant="body2"
            data-testid="stop-packages-toggle"
            aria-expanded={expanded}
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            style={{
              marginTop: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: '#7825bd',
              fontWeight: 700,
              textTransform: 'uppercase',
              fontSize: 12,
              letterSpacing: 0.4,
            }}
          >
            {packagesCountLabel(stop.package_count, translate)}
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Typography>

          {expanded && stop.packages?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {stop.packages.map((pkg) => (
                <PackageCard key={pkg.id} pkg={pkg} showCompany={showCompany} stopAction={stop.action} />
              ))}
            </div>
          )}
        </>
      )}
    </Paper>
  );
}
